import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import CustomerProfileModal from '../../components/CustomerProfileModal';
import { finesService, errorMessage } from '../../services/finesService';
import { usePermissions } from '../../hooks/usePermissions';
import { Notice, Tile } from '../OperationsPortal/components/collectionsShared';
import {
    CELL, OUT_STATE, cellState, closeRows, siteGroups, numbersPayload, carsText, modelNote, modelRows, bookingRows,
    channelList, outRows, chartRows, dayText, dayHead, weekday,
} from './availability.logic';
import Badge from '../../ui/Badge/Badge';
import Button from '../../ui/Button';
import Card from '../../ui/Card';
import Tabs from '../../ui/Tabs/Tabs';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { Input, Select, Checkbox } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellNumber, CellText, CellMuted } from '../../ui/DataTable/cells';
import LoadingSpinner from '../../components/LoadingSpinner';
import { toast } from '../../ui/feedback';

// Operations > Availability (owner, 24 Oct 2026: "عايز أخطط بحيث ميبقاش عندي حجز من غير عربية" - then "اى حد يشوفها
// يفهم ايه اللى موجود وايه اللى محجوز بسهولة ... عايزه للكل"; 25 Oct: tabs, the first one every model's free cars in
// full, the others simpler to read). The airport (Terminal 1 + 2), worked out by the engine from Ejar's cars, contracts
// and bookings: the free cars day by day, every booking (any channel) with its dates and whether it has a car, the
// cars out and when they come back, each model's own days, and the booking sites' stop-sell list (Rentway, by hand).
// Telegram tells the bookings with no car (08:00 + what changes).

// long text wraps (the table's own cells never do): the table stays as wide as the page - one scroll
const WRAP = { whiteSpace: 'normal', minWidth: 130 };
const CHART_DAYS = 30;
// narrow day columns: 30 days fit the page, so the whole table shows with the page's own scroll
const DAY_HEAD = { padding: '0 3px', height: 46 };
const DAY_CELL = { padding: '4px 2px' };
// the first column stays while the days scroll sideways: above the day headers (the kit gives them 2-3), under them for
// the body, and opaque (the kit's zebra rows are see-through) - inline, as the kit's own rules beat classes
const CORNER = { position: 'sticky', left: 0, zIndex: 4 };
const FIRST = { position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#ffffff', padding: '4px 10px' };

// a date in two short lines (the day, then the time): the tables stay as wide as the page
function When({ iso }) {
    if (!iso) return <CellMuted>-</CellMuted>;
    return <><div className="whitespace-nowrap font-semibold">{`${weekday(iso)} ${dayText(iso)}`}</div><CellMuted>{String(iso).slice(11, 16)}</CellMuted></>;
}

function Model({ name, note }) {
    return <><CellText>{name}</CellText>{note && <CellMuted>{note}</CellMuted>}</>;
}

function DayHeader({ day, i }) {
    const h = dayHead(day, i);
    return (
        <TableHeader className="text-center" style={DAY_HEAD}>
            <div>{h.top}</div><div className="text-[10px] font-normal normal-case opacity-80">{h.sub}</div>
        </TableHeader>
    );
}

// what each colour means, next to the table it colours
function Legend({ items }) {
    return (
        <div className="flex flex-wrap justify-center gap-2 text-xs">
            {items.map(([style, label, dark]) => (
                <span key={label} className={`rounded px-2 py-1 font-semibold ${dark ? 'text-white' : ''}`} style={style}>{label}</span>
            ))}
        </div>
    );
}

// every model, day by day: the number = the cars of the model free that day (its busiest moment); a row opens the model
function Calendar({ data, span, onPick }) {
    const days = (data.days || []).slice(0, span);
    return (
        // 30 days fit the page: the page scrolls, the days stay on top; 90 do not: every model still shows (owner: the
        // whole table), the days scroll sideways
        <TableContainer className="overflow-auto" maxHeight={span > CHART_DAYS ? 4000 : undefined}>
            <Table sticky className="min-w-full">
                <TableHead>
                    <TableRow>
                        <TableHeader className="whitespace-nowrap text-center" style={CORNER}>Model</TableHeader>
                        {days.map((d, i) => <DayHeader key={d} day={d} i={i} />)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(data.groups || []).map((g) => (
                        <TableRow key={g.key} className="cursor-pointer text-center hover:brightness-95" onClick={() => onPick(g.key)}>
                            <TableCell className="whitespace-nowrap text-center" style={FIRST}>
                                <div className="font-semibold">{g.name}</div>
                                <div className="text-xs text-gray-600">{`${modelNote(g)} · ${g.cars} car${g.cars === 1 ? '' : 's'}`}</div>
                            </TableCell>
                            {days.map((d, i) => {
                                const state = cellState(g, i);
                                return (
                                    <TableCell key={d} className="text-center text-base font-bold tabular-nums" style={{ ...DAY_CELL, ...CELL[state]?.style }}
                                        title={`${g.name} · ${weekday(d)} ${dayText(d)}: ${g.free[i]} free of ${g.cars} · ${CELL[state]?.label || state}`}>
                                        {g.free[i]}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// every model: what it has now and how the next 90 days look - a row opens the model's own days
function Models({ rows, selected, onPick }) {
    return (
        <TableContainer>
            <Table className="min-w-full">
                <TableHead>
                    <TableRow>
                        {['#', 'Model', 'Cars at the airport', 'Ready now', 'On rent now', 'Bookings coming', 'Next 90 days'].map((h) => (
                            <TableHeader key={h} className="text-center">{h}</TableHeader>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={r.key} className={`cursor-pointer text-center ${selected === r.key ? 'bg-violet-50' : ''}`}
                            onClick={() => onPick(selected === r.key ? '' : r.key)} aria-selected={selected === r.key}>
                            <TableCell><CellNumber value={i + 1} /></TableCell>
                            <TableCell className="whitespace-nowrap"><Model name={r.name} note={r.note} /></TableCell>
                            <TableCell className="ds-cell-num font-bold">{r.cars}</TableCell>
                            <TableCell className="ds-cell-num font-bold text-green-700">
                                {r.ready}{r.held ? <CellMuted>{`+${r.held} held for a booking`}</CellMuted> : null}
                            </TableCell>
                            <TableCell className="ds-cell-num">{r.out}</TableCell>
                            <TableCell className="ds-cell-num">
                                {r.booked}{r.noCar ? <div className="text-xs font-bold text-red-700">{`${r.noCar} with no car`}</div> : null}
                            </TableCell>
                            <TableCell><Badge variant={r.status.tone}>{r.status.text}</Badge></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// every booking picked up at the airport, from any channel, with its dates and whether it has a car
function Bookings({ rows }) {
    if (!rows.length) return <Notice tone="neutral">No booking matches.</Notice>;
    return (
        <TableContainer>
            <Table className="min-w-full">
                <TableHead>
                    <TableRow>
                        {['#', 'Booking', 'Customer', 'Model', 'Came from', 'Pick-up', 'Drop-off', 'Car for it?'].map((h) => (
                            <TableHeader key={h} className="text-center">{h}</TableHeader>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((b, i) => (
                        <TableRow key={b.key} className="text-center">
                            <TableCell><CellNumber value={i + 1} /></TableCell>
                            <TableCell className="whitespace-nowrap font-semibold">
                                {b.id}{b.ref ? <CellMuted>{`Ref ${b.ref}`}</CellMuted> : null}
                            </TableCell>
                            <TableCell style={WRAP}>{b.customer || <CellMuted>-</CellMuted>}</TableCell>
                            <TableCell className="whitespace-nowrap"><Model name={b.model} note={b.note} /></TableCell>
                            <TableCell style={WRAP}>
                                {b.channel}{b.not_on_ejar ? <div><Badge variant="warning" size="sm">Not on Ejar yet</Badge></div> : null}
                            </TableCell>
                            <TableCell><When iso={b.pickup} /></TableCell>
                            <TableCell><When iso={b.dropoff} /></TableCell>
                            <TableCell>
                                {b.no_car
                                    ? <Badge variant="danger">{`No car on ${dayText(b.no_car)}`}</Badge>
                                    : <Badge variant="success">Has a car</Badge>}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// the cars out on rent now and when each comes back
function CarsOut({ rows, onContract }) {
    if (!rows.length) return <Notice tone="neutral">No car matches.</Notice>;
    return (
        <TableContainer>
            <Table className="min-w-full">
                <TableHead>
                    <TableRow>
                        {['#', 'Car', 'Customer', 'Contract', 'Came from', 'Rented since', 'Back on', 'Status'].map((h) => (
                            <TableHeader key={h} className="text-center">{h}</TableHeader>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((c, i) => (
                        <TableRow key={c.key} className="text-center">
                            <TableCell><CellNumber value={i + 1} /></TableCell>
                            <TableCell className="whitespace-nowrap"><div className="font-semibold">{c.plate}</div><CellMuted>{c.model}</CellMuted></TableCell>
                            <TableCell style={WRAP}>{c.customer || <CellMuted>-</CellMuted>}</TableCell>
                            <TableCell className="whitespace-nowrap">
                                {c.contract
                                    ? <button type="button" className="font-semibold text-violet-700 underline" onClick={() => onContract(c)}>{c.contract}</button>
                                    : <CellMuted>-</CellMuted>}
                            </TableCell>
                            <TableCell style={WRAP}>{c.channel || <CellMuted>-</CellMuted>}</TableCell>
                            <TableCell><When iso={c.since} /></TableCell>
                            <TableCell>{c.back ? <When iso={c.back} /> : <CellMuted>-</CellMuted>}</TableCell>
                            <TableCell><Badge variant={OUT_STATE[c.state]?.tone || 'neutral'}>{OUT_STATE[c.state]?.text || c.state}</Badge></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const CHART_CELL = {
    out: { backgroundColor: '#cbd5e1' },
    booking: { backgroundColor: '#c4b5fd' },
    nocar: { backgroundColor: '#dc2626' },
};

// one model, day by day: each car out and each booking as a bar from its first day to its last, the cars free below
function ModelChart({ group, days }) {
    const shown = days.slice(0, CHART_DAYS);
    const rows = chartRows(group, shown);
    return (
        <div className="space-y-3">
            <Legend items={[[CHART_CELL.out, 'Grey bar = a car out on rent'], [CHART_CELL.booking, 'Purple bar = a booking (it takes a car)'],
                [CHART_CELL.nocar, 'Red = days the booking has no car', true]]} />
            {(group.ready_plates?.length > 0 || group.held_plates?.length > 0) && (
                <div className="text-center text-sm">
                    {group.ready_plates?.length > 0 && <span className="mx-2"><b>Ready now:</b> {group.ready_plates.join(', ')}</span>}
                    {group.held_plates?.length > 0 && <span className="mx-2"><b>Held for a booking:</b> {group.held_plates.join(', ')}</span>}
                </div>
            )}
            <TableContainer className="overflow-auto">
                <Table sticky className="min-w-full">
                    <TableHead>
                        <TableRow>
                            <TableHeader className="whitespace-nowrap text-center" style={CORNER}>{`${group.name} · ${group.cars} car${group.cars === 1 ? '' : 's'}`}</TableHeader>
                            {shown.map((d, i) => <DayHeader key={d} day={d} i={i} />)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.key}>
                                <TableCell className="whitespace-nowrap text-center" style={FIRST}>
                                    <div className="font-semibold">{r.title}</div><div className="text-xs text-gray-600">{r.sub}</div>
                                </TableCell>
                                {r.cells.map((kind, i) => (
                                    <TableCell key={shown[i]} style={{ ...DAY_CELL, ...(kind ? CHART_CELL[kind] : {}) }} />
                                ))}
                            </TableRow>
                        ))}
                        <TableRow className="text-center">
                            <TableCell className="whitespace-nowrap text-center font-bold" style={FIRST}>Cars free</TableCell>
                            {shown.map((d, i) => (
                                <TableCell key={d} className="text-center font-bold tabular-nums" style={{ ...DAY_CELL, ...CELL[cellState(group, i)]?.style }}>
                                    {group.free[i]}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

// the booking sites' stop-sell list (Rentway holds no cars) and each group's two limits
function BookingSites({ data, isAdmin, onSaved }) {
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(false);
    const groups = useMemo(() => siteGroups(data.groups), [data]);
    const close = useMemo(() => closeRows(data.groups), [data]);
    const payload = useMemo(() => numbersPayload(groups, edits), [groups, edits]);
    const set = (code, k, v) => setEdits((e) => ({ ...e, [code]: { ...(e[code] || {}), [k]: v } }));
    const save = async () => {
        if (payload.error) { toast.error(payload.error); return; }
        setSaving(true);
        try {
            const out = await finesService.rentwayAvailabilitySave(payload.numbers);
            toast.success(out.changes?.length ? `Saved ${out.changes.length} change(s)` : 'Nothing changed');
            setEdits({});
            onSaved();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSaving(false); }
    };
    return (
        <div className="space-y-4">
            <Card title={`Stop selling on Rentway: ${close.length} period${close.length === 1 ? '' : 's'}`}
                subtitle="Close these car groups on Rentway for these days (by hand for now) - the booking sites must not sell them">
                {close.length ? (
                    <TableContainer>
                        <Table className="min-w-full">
                            <TableHead>
                                <TableRow>
                                    {['#', 'Car group', 'Stop selling from', 'Until', 'Days', 'Why'].map((h) => <TableHeader key={h} className="text-center">{h}</TableHeader>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {close.map((r, i) => (
                                    <TableRow key={r.key} className="text-center">
                                        <TableCell><CellNumber value={i + 1} /></TableCell>
                                        <TableCell className="whitespace-nowrap"><CellText>{r.name}</CellText><CellMuted>{r.code}</CellMuted></TableCell>
                                        <TableCell className="whitespace-nowrap font-semibold">{dayText(r.from)}</TableCell>
                                        <TableCell className="whitespace-nowrap font-semibold">{dayText(r.to)}</TableCell>
                                        <TableCell className="ds-cell-num">{r.days}</TableCell>
                                        <TableCell style={WRAP}>{r.why}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : <Notice tone="success">Every car group is open to the booking sites for the next 90 days.</Notice>}
            </Card>
            <Card title="Limits per car group"
                subtitle="Booking sites limit = the most cars the booking sites may hold on one day · Branch keeps = cars always left for the branch's own customers"
                actions={isAdmin ? (
                    <Button variant="primary" size="sm" onClick={save} disabled={saving || !payload.count || !!payload.error}>
                        {saving ? 'Saving…' : `Save${payload.count ? ` (${payload.count})` : ''}`}
                    </Button>
                ) : null}>
                {payload.error && <div className="mb-3"><Notice tone="danger">{payload.error}</Notice></div>}
                <TableContainer>
                    <Table className="min-w-full">
                        <TableHead>
                            <TableRow>
                                {['#', 'Car group', 'Cars (Ejar now)', 'Booking sites limit', 'Branch keeps', 'Closed days (90)'].map((h) => (
                                    <TableHeader key={h} className="text-center">{h}</TableHeader>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groups.map((g, i) => (
                                <TableRow key={g.code} className="text-center">
                                    <TableCell><CellNumber value={i + 1} /></TableCell>
                                    <TableCell className="whitespace-nowrap"><CellText>{g.name}</CellText><CellMuted>{g.code}</CellMuted></TableCell>
                                    <TableCell className="text-sm" style={WRAP}>{carsText(g)}</TableCell>
                                    {['cap', 'reserve'].map((k) => (
                                        <TableCell key={k} className="w-36">
                                            {isAdmin ? (
                                                <div className="mx-auto w-20">
                                                    <Input aria-label={`${g.name} ${k === 'cap' ? 'booking sites limit' : 'branch keeps'}`} inputMode="numeric"
                                                        className="text-center" value={edits[g.code]?.[k] ?? String(g[k])}
                                                        onChange={(e) => set(g.code, k, e.target.value)} />
                                                </div>
                                            ) : <span className="ds-cell-num font-bold">{g[k]}</span>}
                                        </TableCell>
                                    ))}
                                    <TableCell className="ds-cell-num">{g.site_states.filter((s) => s !== 'open').length}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {(data.history || []).length > 0 && (
                    <div className="mt-3 text-center text-xs text-gray-600">
                        <div className="font-semibold">Last changes</div>
                        {data.history.slice(0, 5).map((h) => (
                            <div key={`${h.at}${h.group}${h.key}`}>
                                {`${h.at} · ${h.by} · ${h.group} ${h.key === 'cap' ? 'booking sites limit' : 'branch keeps'} ${h.from} → ${h.to}`}
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}

// one plain sentence on top of each tab: what it shows and how to read it
function HowToRead({ children }) {
    return <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-center text-sm text-violet-900">{children}</div>;
}

export function Availability() {
    const { isAdmin } = usePermissions();
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('free');
    const [span, setSpan] = useState('30');
    const [model, setModel] = useState('');              // the Models tab's picked model (its days)
    const [bookModel, setBookModel] = useState('');
    const [channel, setChannel] = useState('');
    const [words, setWords] = useState('');
    const [noCar, setNoCar] = useState(false);
    const [outModel, setOutModel] = useState('');
    const [outWords, setOutWords] = useState('');
    const [profile, setProfile] = useState(null);
    const load = useCallback(async () => {
        setLoading(true); setErr('');
        try { setData(await finesService.availability()); } catch (e) { setErr(errorMessage(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const groups = useMemo(() => data?.groups || [], [data]);
    const models = useMemo(() => modelRows(groups, data?.days), [groups, data]);
    const channels = useMemo(() => channelList(groups), [groups]);
    // a filter whose choice is gone after a refresh filters nothing (its Select shows "Every ..." then)
    const has = (key) => groups.some((g) => g.key === key);
    const bm = has(bookModel) ? bookModel : '';
    const om = has(outModel) ? outModel : '';
    const ch = channels.includes(channel) ? channel : '';
    const bookings = useMemo(() => bookingRows(groups, { model: bm, channel: ch, words, noCar }), [groups, bm, ch, words, noCar]);
    const out = useMemo(() => outRows(groups, om, outWords), [groups, om, outWords]);
    const modelOptions = useMemo(() => groups.map((g) => ({ value: g.key, label: g.name })), [groups]);
    const picked = groups.find((g) => g.key === model) || null;
    if (loading && !data) return <LoadingSpinner message="Reading the airport's cars and bookings…" />;
    const all = bookingRows(groups);
    const noCarCount = all.filter((b) => b.no_car).length;
    const outCount = groups.reduce((n, g) => n + (g.out || []).length, 0);
    const close = closeRows(groups);
    const openModel = (key) => {                          // a calendar row: that model's own days, from the top
        setModel(key); setTab('models');
        const top = document.getElementById('avail-top');
        if (top && top.scrollIntoView) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const tiles = (id) => {                                // a tile opens the tab it counts, every filter off
        if (id === 'nocar' || id === 'bookings') {
            setBookModel(''); setChannel(''); setWords(''); setNoCar(id === 'nocar'); setTab('bookings');
        } else if (id === 'out') { setOutModel(''); setOutWords(''); setTab('out'); }
        else if (id === 'models') { setModel(''); setTab('models'); }
        else setTab(id);
    };
    return (
        <div id="avail-top" className="space-y-4">
            <Card title="Airport cars and bookings"
                subtitle={`Terminal 1 + Terminal 2, from Ejar${data?.now ? ` · read ${data.now.replace('T', ' ').slice(0, 16)}` : ''} · a car is free again the moment it is due back`}
                actions={(
                    <Button variant="primary" size="sm" onClick={load} disabled={loading}
                        leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>{loading ? 'Reading…' : 'Refresh'}</Button>
                )}>
                <div className="space-y-3">
                    {err && <Notice tone="danger">{err}</Notice>}
                    {data && (
                        <div className="flex flex-wrap justify-center gap-2">
                            <Tile label="Cars at the airport" value={groups.reduce((n, g) => n + g.cars, 0)} id="free" onPick={tiles} hint="free cars day by day" />
                            <Tile label="Ready now" value={groups.reduce((n, g) => n + (g.ready || 0), 0)} tone="text-green-700" id="models" onPick={tiles} hint="each model" />
                            <Tile label="On rent now" value={outCount} id="out" onPick={tiles} hint="the cars on rent" />
                            <Tile label="Bookings coming (90 days)" value={all.length} id="bookings" onPick={tiles} hint="every booking" />
                            <Tile label="Bookings with no car" value={noCarCount} tone={noCarCount ? 'text-red-700' : 'text-green-700'} id="nocar" onPick={tiles} hint="the bookings with no car" />
                        </div>
                    )}
                    {data && (
                        <Tabs label="Availability" idPrefix="avail-tab" value={tab} onChange={setTab}
                            items={[
                                { id: 'free', label: 'Free cars' },
                                { id: 'bookings', label: 'Bookings', count: all.length },
                                { id: 'out', label: 'Cars on rent', count: outCount },
                                { id: 'models', label: 'Each model' },
                                { id: 'sites', label: 'Booking sites (Rentway)', count: close.length },
                            ]} />
                    )}
                </div>
            </Card>

            {data && tab === 'free' && (
                <div id="avail-tab-panel-free" role="tabpanel" className="space-y-3">
                    <HowToRead>
                        Each number = how many cars of that model are <b>still free</b> on that day. Click a model to see its cars and bookings.
                    </HowToRead>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <Tabs variant="segmented" label="Days shown" idPrefix="avail-span" value={span} onChange={setSpan}
                            items={[{ id: '30', label: 'Next 30 days' }, { id: '90', label: 'Next 90 days' }]} />
                        <Legend items={[[CELL.ok.style, 'Green = cars free'], [CELL.last.style, 'Yellow = last car: one more booking has no car'],
                            [CELL.short.style, 'Red = a booking has no car', true], [CELL.none.style, 'Grey = no car of it at the airport']]} />
                    </div>
                    <div id={`avail-span-panel-${span}`} role="tabpanel">
                        <Calendar data={data} span={Number(span)} onPick={openModel} />
                    </div>
                </div>
            )}

            {data && tab === 'bookings' && (
                <div id="avail-tab-panel-bookings" role="tabpanel" className="space-y-3">
                    <HowToRead>
                        Every booking to be picked up at the airport, from <b>every channel</b> (branch, app, website, booking sites).
                        {' '}<b>Has a car</b> = a car will be free for it · <b>No car</b> = on that day every car of its model is taken.
                    </HowToRead>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <div className="w-56"><Input aria-label="Search bookings" placeholder="Booking, customer, ref…" value={words} onChange={(e) => setWords(e.target.value)} /></div>
                        <div className="w-56">
                            <Select aria-label="Model" value={bm} onChange={(e) => setBookModel(e.target.value)} placeholder="Every model" options={modelOptions} />
                        </div>
                        <div className="w-56">
                            <Select aria-label="Came from" value={ch} onChange={(e) => setChannel(e.target.value)} placeholder="Every channel"
                                options={channels.map((c) => ({ value: c, label: c }))} />
                        </div>
                        <Checkbox label="Only bookings with no car" checked={noCar} onChange={(e) => setNoCar(e.target.checked)} />
                        <span className="text-sm font-semibold text-gray-700">{`${bookings.length} booking${bookings.length === 1 ? '' : 's'}`}</span>
                    </div>
                    <Bookings rows={bookings} />
                </div>
            )}

            {data && tab === 'out' && (
                <div id="avail-tab-panel-out" role="tabpanel" className="space-y-3">
                    <HowToRead>
                        The airport's cars out with customers now and the day each one <b>comes back</b> (free again from that moment).
                        {' '}<b>Not back yet</b> = its return time has passed but Ejar still shows it out - it stays busy until it is returned.
                    </HowToRead>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <div className="w-56"><Input aria-label="Search cars on rent" placeholder="Plate, customer, contract…" value={outWords} onChange={(e) => setOutWords(e.target.value)} /></div>
                        <div className="w-56">
                            <Select aria-label="Model of the cars on rent" value={om} onChange={(e) => setOutModel(e.target.value)} placeholder="Every model" options={modelOptions} />
                        </div>
                        <span className="text-sm font-semibold text-gray-700">{`${out.length} car${out.length === 1 ? '' : 's'}`}</span>
                    </div>
                    <CarsOut rows={out} onContract={(c) => setProfile({ contractNo: c.contract, name: c.customer || '', plate: c.plate, model: c.model })} />
                </div>
            )}

            {data && tab === 'models' && (
                <div id="avail-tab-panel-models" role="tabpanel" className="space-y-3">
                    {picked ? (
                        <Card title={`${picked.name}: day by day`} subtitle={`The next ${CHART_DAYS} days - each car on rent and each booking from its first day to its last`}
                            actions={(
                                <div className="flex items-center gap-2">
                                    <div className="w-52"><Select aria-label="Show another model" value={model} onChange={(e) => setModel(e.target.value || model)} options={modelOptions} /></div>
                                    <Button variant="secondary" size="sm" onClick={() => setModel('')}>Back to every model</Button>
                                </div>
                            )}>
                            <ModelChart group={picked} days={data.days || []} />
                        </Card>
                    ) : (
                        <>
                            <HowToRead>
                                Each model: its cars at the airport, how many are ready now, on rent, and its bookings coming.
                                {' '}<b>Click a model</b> to see its cars and bookings day by day.
                            </HowToRead>
                            <Models rows={models} selected={model} onPick={setModel} />
                        </>
                    )}
                </div>
            )}

            {data && tab === 'sites' && (
                <div id="avail-tab-panel-sites" role="tabpanel" className="space-y-3">
                    <HowToRead>
                        Rentway sells our cars to the booking sites (DiscoverCars, CARJET…) but does <b>not</b> know which cars are free.
                        {' '}This tab says when to <b>stop selling</b> a car group on Rentway, so no booking comes with no car.
                    </HowToRead>
                    <BookingSites data={data} isAdmin={isAdmin} onSaved={load} />
                </div>
            )}
            <CustomerProfileModal open={!!profile} seed={profile} onClose={() => setProfile(null)} onCopy={(msg) => toast.success(msg || 'Copied')} />
        </div>
    );
}

export default function AvailabilityPage() {
    return (
        <Layout title="Availability" subtitle="The airport's cars and bookings: what is free, what is booked, from when to when">
            <div className="w-full mx-auto"><Availability /></div>
        </Layout>
    );
}
